import { useCallback, useEffect, useState } from 'react';
import { Calendar, Stethoscope, Image as ImageIcon, PawPrint } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPet, listPetMedicalRecords } from '../api/pets';
import { listAppointments } from '../api/appointments';
import { getErrorMessage } from '../api/response';
import Modal from './Modal';
import { LoadingState, EmptyState } from './PageState';
import { date, dateTime } from '../utils/format';
import { getStatusConfig, appointmentStatus } from '../utils/status';
import StatusBadge from './StatusBadge';

function sterilizedLabel(value) {
  if (value === 1 || value === true) return 'Castrada / operada';
  if (value === 0 || value === false) return 'No castrada / no operada';
  return 'Sin informar';
}

function InfoItem({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</p>
      <p className="text-sm text-gray-950 break-words">{value || '—'}</p>
    </div>
  );
}

export default function PetDetailModal({ pet: initialPet, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [pet, setPet] = useState(null);
  const [records, setRecords] = useState([]);
  const [appointments, setAppointments] = useState([]);

  const loadData = useCallback(async () => {
    const petRef = initialPet?.id || initialPet?.uuid;
    if (!petRef) return;

    try {
      setLoading(true);
      const petData = await getPet(petRef);
      const [recordData, appointmentData] = await Promise.all([
        listPetMedicalRecords(petData.id),
        listAppointments({ pet_id: petData.id, limit: 100 })
      ]);
      setPet(petData);
      setRecords(recordData);
      setAppointments(appointmentData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar la ficha de la mascota'));
      setPet(initialPet || null);
      setRecords([]);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [initialPet]);

  useEffect(() => {
    if (open && initialPet) {
      loadData();
    } else if (!open) {
      setPet(null);
      setRecords([]);
      setAppointments([]);
    }
  }, [open, initialPet, loadData]);

  const upcomingAppointments = appointments.filter((a) => new Date(a.starts_at) > new Date());
  const pastAppointments = appointments.filter((a) => new Date(a.starts_at) <= new Date());

  return (
    <Modal
      open={open}
      title={pet?.name || initialPet?.name || 'Ficha de mascota'}
      onClose={onClose}
      size="xl"
    >
      {loading && !pet ? (
        <LoadingState rows={6} />
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
          {/* Columna izquierda: foto + datos */}
          <aside className="w-full shrink-0 space-y-4 lg:w-72 xl:w-80">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-100 aspect-square max-h-80 lg:max-h-none lg:aspect-[4/5]">
              {pet?.photo_url ? (
                <img
                  src={pet.photo_url}
                  alt={pet.name}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="flex h-full min-h-[200px] flex-col items-center justify-center text-gray-400">
                  <ImageIcon className="h-10 w-10 mb-2" />
                  <p className="text-sm">Sin foto</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-600">
              <PawPrint className="h-4 w-4 text-blue-600 shrink-0" />
              <span>
                {pet?.species || 'Especie no indicada'}
                {(pet?.breed || pet?.breed_label) && ` · ${pet.breed || pet.breed_label}`}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
              <InfoItem label="Raza" value={pet?.breed || pet?.breed_label} />
              <InfoItem label="Color" value={pet?.color} />
              <InfoItem label="Microchip" value={pet?.microchip} />
              <InfoItem label="Esterilización" value={pet?.sterilized_label || sterilizedLabel(pet?.is_sterilized)} />
              <InfoItem
                label="Sexo"
                value={pet?.sex === 'male' ? 'Macho' : pet?.sex === 'female' ? 'Hembra' : null}
              />
              <InfoItem label="Nacimiento" value={pet?.birth_date ? date(pet.birth_date) : null} />
              <InfoItem label="Peso" value={pet?.weight ? `${pet.weight} kg` : null} />
              {pet?.notes && (
                <div className="sm:col-span-2 lg:col-span-1">
                  <InfoItem label="Observaciones" value={pet.notes} />
                </div>
              )}
            </div>

            {(upcomingAppointments.length > 0 || pastAppointments.length > 0) && (
              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                <h3 className="text-sm font-semibold text-gray-950 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-green-600" />
                  Citas
                </h3>
                {upcomingAppointments.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Próximas</p>
                    <ul className="space-y-1">
                      {upcomingAppointments.slice(0, 3).map((apt) => (
                        <li key={apt.id} className="text-xs text-gray-700">
                          {dateTime(apt.starts_at)} · {apt.service_name || 'Consulta'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {pastAppointments.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Anteriores</p>
                    <ul className="space-y-1 max-h-24 overflow-y-auto">
                      {pastAppointments.slice(0, 5).map((apt) => (
                        <li key={apt.id} className="text-xs text-gray-600">
                          {dateTime(apt.starts_at)} · {apt.service_name || 'Consulta'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </aside>

          {/* Columna derecha: historial clínico amplio */}
          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-950">
              <Stethoscope className="h-5 w-5 text-blue-600" />
              Historial clínico
            </h3>

            <div className="min-h-[320px] flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50/50 p-3 lg:max-h-[calc(90vh-12rem)]">
              {loading ? (
                <LoadingState rows={4} />
              ) : records.length ? (
                <div className="space-y-3">
                  {records.map((record) => (
                    <article
                      key={record.id}
                      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="font-semibold text-gray-950 text-sm flex-1">
                          {record.diagnosis || record.description || 'Registro clínico'}
                        </p>
                        <span className="text-xs text-gray-500 whitespace-nowrap shrink-0">
                          {dateTime(record.created_at)}
                        </span>
                      </div>
                      {record.clinical_details && (
                        <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">
                          {record.clinical_details}
                        </p>
                      )}
                      {record.treatments && (
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Tratamiento:</span> {record.treatments}
                        </p>
                      )}
                      {(record.weight || record.temperature) && (
                        <p className="text-xs text-gray-500">
                          {record.weight && `Peso: ${record.weight} kg`}
                          {record.weight && record.temperature && ' · '}
                          {record.temperature && `Temp: ${record.temperature}°C`}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="Sin registros clínicos"
                  message="Aún no hay atenciones documentadas para esta mascota."
                />
              )}
            </div>

            {pastAppointments.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Detalle de citas anteriores</h4>
                <div className="max-h-40 space-y-2 overflow-y-auto">
                  {pastAppointments.map((apt) => {
                    const status = getStatusConfig(appointmentStatus, apt.status);
                    return (
                      <div
                        key={apt.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900">{dateTime(apt.starts_at)}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {apt.service_name || `Servicio #${apt.service_id}`}
                          </p>
                        </div>
                        <StatusBadge label={status.label} color={status.color} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}
